import { useEffect, useMemo, useState } from "react";

import type { Dayjs } from "dayjs";
import { useCurrentStateAndParams } from "@uirouter/react";
import isEmpty from "lodash/isEmpty";
import maxBy from "lodash/maxBy";
import styled from "styled-components";

import { Flex } from "@similarweb/ui-components/foundations";

import type { ICategory } from "app/common/services/categoryService.types";
import { chosenSites } from "app/common/services/chosenSites";
import { CHART_COLORS } from "app/constants/ChartColors";
import { minVisitsAbbrFilter } from "app/filters/ngFilters";
import { orderedTrafficSources } from "app/Shared/utils";

import type { ISiteLegendItem } from "../ChannelAnalysisChart/ChannelAnalysisChartContainer";
import { getChartConfigCompareMode } from "./AllTrafficChannelsOverviewBarChartUtils";
import { getPointY, sumObject } from "./AllTrafficChannelsOverviewBarChartShared";
import { ChannelsOverviewBarChart } from "./ChannelsOverviewBarChart";
import { ChannelsOverviewBarChartCategoryBenchmark } from "./ChannelsOverviewBarChartCategoryBenchmark";
import { translateSourceText } from "./ChannelsOverviewBarChartConfig";
import type { DataFormat } from "./ChannelsOverviewBarChartContainerFormat";
import { dataModeMap } from "./ChannelsOverviewBarChartContainerFormat";
import { ChannelsOverviewBarChartTopLegends } from "app/components/React/ChannelsOverviewBarChartTopLegends/ChannelsOverviewBarChartTopLegends";
import { allTrackers } from "app/services/track/track";

interface ILegendItem extends Partial<ISiteLegendItem> {
    isCategory?: boolean;
    data: number;
    visible: boolean;
}
interface IAllTrafficChannelsOverviewBarChartCompareMode {
    isCategoryBenchmarkVisible: boolean;
    data: any;
    dataFormat: DataFormat;
    fromDate: Dayjs;
    onCategorySelected: (category: ICategory | null) => void;
}

const ChartContainer = styled(Flex)`
    flex-direction: column;
    gap: 16px;
    padding: 24px;
`;

const ChartWrapper = styled.div`
    flex: 1;
`;

export const AllTrafficChannelsOverviewBarChartCompareMode = ({
    isCategoryBenchmarkVisible,
    data,
    dataFormat,
    fromDate,
    onCategorySelected,
}: IAllTrafficChannelsOverviewBarChartCompareMode) => {
    const { params } = useCurrentStateAndParams();
    const { key, category, duration } = params;
    const isCompare = key.split(",").length > 1;
    const [seriesCompareMode, setSeriesCompareMode] = useState(null);
    const [legendsCompareMode, setLegendsCompareMode] = useState<ILegendItem[]>(
        chosenSites.sitelistForLegend().map((item) => ({
            name: item.name,
            color: item.color,
            visible: true,
            data: null,
            isWinner: null,
        })),
    );

    const getWinner = () => {
        return maxBy(Object.keys(data.Total), (o) => {
            return sumObject(data.Total[o]);
        });
    };
    useEffect(() => {
        if (data) {
            const updatedLegends = [];
            const winner = getWinner();
            legendsCompareMode.forEach((legend) => {
                const totalVisits = sumObject(data.Total[legend.name]);
                updatedLegends.push({
                    ...legend,
                    data: totalVisits ? minVisitsAbbrFilter()(totalVisits) : "N/A",
                    isWinner: legend.name === winner,
                });
            });

            setLegendsCompareMode(updatedLegends);
        }
    }, [data]);

    const configCompareMode = useMemo(
        () =>
            data &&
            getChartConfigCompareMode({
                data,
                legends: legendsCompareMode,
                dataFormat,
                fromDate,
            }),
        [data, legendsCompareMode, fromDate, dataFormat],
    );

    useEffect(() => {
        if (isEmpty(data)) {
            setSeriesCompareMode({});
        } else {
            // Build the union of sources present across all sites, in priority order.
            // This mirrors the xAxis categories so each site's data array aligns by index.
            const sourcesPresentInData = new Set<string>();
            Object.values(data.Total ?? {}).forEach((domainSources: Record<string, number>) => {
                Object.keys(domainSources ?? {}).forEach((s) => sourcesPresentInData.add(s));
            });
            const alignedSources = orderedTrafficSources.filter((source) => sourcesPresentInData.has(source));

            const sitesDataPoints = key
                .split(",")
                .map((domainName) => {
                    if (data.Total[domainName]) {
                        const totalVisits = sumObject(data.Total[domainName]);
                        if (legendsCompareMode.find((l: ILegendItem) => l.name === domainName)?.visible) {
                            const getFullDataForWebsite = (device) => {
                                return alignedSources.map((sourceName) => {
                                    const hasSource = Object.prototype.hasOwnProperty.call(
                                        data[device][domainName],
                                        sourceName,
                                    );
                                    return {
                                        key: translateSourceText(sourceName),
                                        y: hasSource
                                            ? getPointY(data[device][domainName][sourceName], totalVisits, dataFormat)
                                            : null,
                                        color: isCompare
                                            ? chosenSites.getSiteColor(domainName)
                                            : CHART_COLORS.trafficSourcesColorsBySourceMMX[sourceName],
                                    };
                                });
                            };
                            return {
                                name: domainName,
                                data: getFullDataForWebsite("Total"),
                            };
                        }
                    }
                })
                .filter(Boolean);
            setSeriesCompareMode([...sitesDataPoints]);
        }
    }, [data, dataFormat, fromDate, isCompare, key, legendsCompareMode]);

    const handleLegendsChange = (changedLegend: ILegendItem) => {
        setLegendsCompareMode((legends) =>
            legends.map((legend) =>
                legend.name === changedLegend.name ? { ...legend, visible: !legend.visible } : legend,
            ),
        );

        allTrackers.dispatchTrackEvent(
            "Graph Filter",
            "click",
            `Channel Overview graph/MMX/All Traffic/${changedLegend.name || ""}/Legend/${changedLegend.visible ? "add" : "remove"}`,
        );
    };

    return (
        <ChartContainer data-automation="allTrafficChannelsOverviewBarChartCompareMode">
            {isCategoryBenchmarkVisible ? (
                <ChannelsOverviewBarChartCategoryBenchmark
                    isUnbound={false}
                    categoryParam={category}
                    dataMode={dataModeMap[dataFormat]}
                    durationParam={duration}
                    onCategorySelected={onCategorySelected}
                />
            ) : null}
            {legendsCompareMode && legendsCompareMode.length > 0 ? (
                <ChannelsOverviewBarChartTopLegends
                    legendItems={legendsCompareMode}
                    onLegendsChange={handleLegendsChange}
                />
            ) : null}
            <ChartWrapper>
                <ChannelsOverviewBarChart
                    data={seriesCompareMode}
                    config={configCompareMode}
                    dataAutomation="ChannelsOverviewBarChartAllTraffic"
                    stackColumns={false}
                />
            </ChartWrapper>
        </ChartContainer>
    );
};
